import * as THREE from 'three'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { buildingDisplayName } from './buildingNames'
import { isOpaqueCode, FACADE_STYLE, facadeStyleEnabled, FOCUS_EMISSIVE } from './facadeStyle'
import { SHADOWS } from './sceneLights'
import { rippleRadiusFromSphere } from './buildingRipple'

/**
 * 加载 CesiumLab 生成的本地 3D Tiles（tileset.json + glb 切片），
 * 并把所有分片对齐到统一坐标系（对齐方式参考 factory-building-pc 项目 parkViewer 的实现）：
 *   1. 各分片 GLB 内部 rootNode 自带 Z-up→Y-up 翻转矩阵 rotX(-90°)（无平移），
 *      GLTFLoader 加载时会应用它 → gltf.scene 即「分片自身 ENU 坐标系的 Y-up 内容」
 *   2. 每个 tileset 的 root.transform 是 「局部ENU坐标 -> ECEF地心坐标」 的矩阵，
 *      用 inverse(T_part1) * T_partN 把所有分片统一到 part1 的 ENU 局部坐标系
 *   3. 合成变换 M = rotX(-90°) * inverse(T_part1) * T_partN * rotX(+90°)：
 *      末尾 rotX(+90°) 抵消 gltf.scene 内部 rootNode 的翻转（两者相消），
 *      因此 part1 的合成矩阵恰为单位阵 —— 与参考项目一样"直接叠加"；
 *      其余分片仅得到相对 part1 的平移/微小旋转偏移
 *   4. 整体平移，使模型包围盒中心落在原点、地面贴在 y=0
 *
 * ── 关于「地图中心锚点」的坐标系推导（曾经错在这里，务必看完）────────────────────
 *   a. 每个分片的内容在 cityGroup 里最终是 C 空间：C = (东, 上, -北)  ← 由 (2)(3) 推出
 *      （cityGroup 外包一层 rotX(+90°) 后变成世界 ENU：世界 = (东, 北, 上)）
 *   b. 第 4 步用 C 空间的包围盒做平移：offset = (-center.x, -box.min.y, -center.z)
 *      把「C 空间包围盒中心」平移到 C 原点、把「C 空间最低点」平移到 y=0
 *   c. 于是三维场景原点 (0,0,0) 对应的 C 空间点是 (center.x, box.min.y, center.z)，
 *      换算成「**基准分片 part1 的 ENU 坐标**」= (center.x, -center.z, box.min.y)
 *   d. 高德 GLCustomLayer 里 customCoords.setCenter(锚点) 是把三维场景原点钉在这个经纬度上，
 *      所以地图中心必须用 (c) 这个点换算出来的经纬度，
 *      而不是「part1 自己的 ENU 原点」—— 本项目四个分片（part1/part2/part3/周边白模）
 *      的 ENU 原点互不重合，最大相差 380 米，用错会让模型整体偏移约 300 米。
 */

const _V = new THREE.Vector3()

const IDENTITY_MATRIX = [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1]

function tilesetModules() {
  // webpack: 打包 20260913data 下所有 tileset json（含 tileset_modified.json，排除 scenetree）
  const ctx = require.context('@/assets/20260913data', true, /\.json$/)
  const map = {}
  ctx.keys().forEach((key) => {
    const base = key.split('/').pop()
    if (!/^tileset[\w-]*\.json$/.test(base)) return
    const mod = ctx(key)
    map[key] = mod && mod.default ? mod.default : mod
  })
  return map
}

// scenetree 按目录路径索引：'20260913part1/' -> scenetree.json 内容
function scenetreeByDir() {
  const ctx = require.context('@/assets/20260913data', true, /\.json$/)
  const map = {}
  ctx.keys().forEach((key) => {
    const base = key.split('/').pop()
    if (!/^scenetree[\w-]*\.json$/.test(base)) return
    const mod = ctx(key)
    const dir = key.replace('./', '').slice(0, key.replace('./', '').lastIndexOf('/') + 1)
    map[dir] = mod && mod.default ? mod.default : mod
  })
  return map
}

function glbUrlResolver() {
  const ctx = require.context('@/assets/20260913data', true, /\.glb$/)
  const map = {}
  ctx.keys().forEach((key) => {
    map[key.replace('./', '')] = ctx(key) // '20260913part1/NoLod_0.glb' -> url
  })
  return (tilesetKey, uri) => {
    const key = tilesetKey.replace('./', '')
    // 按最后一个 "/" 截取目录，兼容 tileset.json / tileset_modified.json 等任意命名
    const dir = key.slice(0, key.lastIndexOf('/') + 1)
    const rel = dir + uri
    if (!map[rel]) {
      console.warn('[loadCityTiles] 未找到 GLB 资源:', rel)
      return null
    }
    return map[rel]
  }
}

// 递归收集 tileset 中所有 content.uri
function collectTileUris(node, out) {
  if (!node) return out
  if (node.content && node.content.uri) out.push(node.content.uri)
  if (node.children) node.children.forEach((c) => collectTileUris(c, out))
  return out
}

// ECEF -> 经纬度（Bowring 公式，WGS84）
export function ecefToLLA(x, y, z) {
  const a = 6378137.0
  const b = 6356752.31424518
  const e2 = (a * a - b * b) / (a * a)
  const ep2 = (a * a - b * b) / (b * b)
  const p = Math.hypot(x, y)
  const th = Math.atan2(a * z, b * p)
  const st = Math.sin(th)
  const ct = Math.cos(th)
  const lat = Math.atan2(z + ep2 * b * st * st * st, p - e2 * a * ct * ct * ct)
  const lon = Math.atan2(y, x)
  const N = a / Math.sqrt(1 - e2 * Math.sin(lat) * Math.sin(lat))
  const alt = p / Math.cos(lat) - N
  return { lat: (lat * 180) / Math.PI, lon: (lon * 180) / Math.PI, alt }
}

function parseTransform(arr) {
  // 3D Tiles transform 为列主序 16 元素数组，与 three.js Matrix4.fromArray 一致
  return new THREE.Matrix4().fromArray(arr)
}

/**
 * 收集全部 tileset 分片元数据（只读 json，不加载 GLB），按 key 排序 —— part1 恒为基准分片。
 * 排序规则与 loadCityTiles 保持一致：'20260913part1' < '20260913part2' < ... < '周边白模'
 */
function collectParts() {
  const jsonMap = tilesetModules()
  const trees = scenetreeByDir()
  const entries = Object.entries(jsonMap).sort(([a], [b]) => (a < b ? -1 : 1))
  if (!entries.length) throw new Error('未在 src/assets/20260913data 下找到 tileset.json')
  return entries.map(([key, json]) => {
    const root = json.root || {}
    const dir = key.replace('./', '').slice(0, key.replace('./', '').lastIndexOf('/') + 1)
    return {
      key,
      dir,
      transform: parseTransform(root.transform || IDENTITY_MATRIX),
      // 3D Tiles 包围盒：[中心(3), 东半轴(3), 北半轴(3), 上半轴(3)]，本数据均为轴对齐
      box: (root.boundingVolume && root.boundingVolume.box) || null,
      uris: collectTileUris(root, []),
      // scenetree：里面每个 type=element 的节点带 name（建筑名）和 sphere（ECEF 球心 + 半径）
      tree: trees[dir] || null,
    }
  })
}

// 分片局部包围盒的 8 个角点 -> 基准分片(part1) 的 ENU 坐标（x=东, y=北, z=上）
function partBoxCornersInBaseENU(part, baseInv) {
  const b = part.box
  if (!b) return []
  const corners = []
  for (let i = 0; i < 8; i++) {
    const p = new THREE.Vector3(
      b[0] + (i & 1 ? b[3] : -b[3]),
      b[1] + (i & 2 ? b[7] : -b[7]),
      b[2] + (i & 4 ? b[11] : -b[11]),
    )
    p.applyMatrix4(part.transform) // 分片局部 ENU -> ECEF
    p.applyMatrix4(baseInv) // ECEF -> 基准分片 ENU
    corners.push(p)
  }
  return corners
}

// 基准分片 ENU 坐标 -> WGS84 经纬度
function baseENUToLLA(point, baseTransform) {
  _V.copy(point).applyMatrix4(baseTransform) // 基准 ENU -> ECEF
  return ecefToLLA(_V.x, _V.y, _V.z)
}

/**
 * 「场景原点」在基准分片 ENU 坐标系里的坐标（不含人工校准偏移）：
 * 模型在东西/南北方向的包围盒中心 + 模型最低高度。
 * 这是三维场景 (0,0,0) 真正代表的地理位置，也是高德地图该用的中心点。
 * @returns {THREE.Vector3} (东, 北, 最低高度) 单位米
 */
export function resolveAnchorENU() {
  const parts = collectParts()
  const baseInv = parts[0].transform.clone().invert()
  const box = new THREE.Box3()
  parts.forEach((part) => {
    partBoxCornersInBaseENU(part, baseInv).forEach((v) => box.expandByPoint(v))
  })
  if (box.isEmpty()) throw new Error('tileset 缺少 boundingVolume.box，无法解算模型位置')
  const c = box.getCenter(new THREE.Vector3())
  return new THREE.Vector3(c.x, c.y, box.min.z)
}

/**
 * 由 tileset 元数据估算「模型锚点」的真实经纬度（WGS84）。不加载 GLB，很快。
 * 用于在模型加载前先把高德地图定位到模型所在地（地图构造需要 center）。
 * 返回的是「模型包围盒中心」的经纬度，绝不是某个分片自身的 ENU 原点。
 * @returns {{lat:number, lon:number, alt:number}}
 */
export function resolveCityCenter() {
  const parts = collectParts()
  return baseENUToLLA(resolveAnchorENU(), parts[0].transform)
}

/**
 * 所有带名称的建筑（= 真正要展示的楼）的中心点经纬度，用于**页面刷新后的默认视角**：
 * 取这些楼在基准分片 ENU 里位置的平均值（东西/南北均值，高度取地面）。
 * 注意与 resolveCityCenter() 的区别：
 *   · resolveCityCenter() 是「对齐锚点」（含周边白模的整块模型包围盒中心），
 *     决定三维场景原点钉在哪个经纬度上，动它会让模型整体偏移；
 *   · 本函数只是「视角中心」，随便移到哪儿都不影响模型与底图的贴合关系。
 * @returns {{lat:number, lon:number, alt:number}}
 */
export function resolveBuildingCenter() {
  const parts = collectParts()
  const baseInv = parts[0].transform.clone().invert()
  const anchors = collectLabelAnchors(parts, baseInv)
  if (!anchors.length) return baseENUToLLA(resolveAnchorENU(), parts[0].transform)
  let sumE = 0
  let sumN = 0
  anchors.forEach((a) => {
    sumE += a.enu.x
    sumN += a.enu.y
  })
  const center = new THREE.Vector3(sumE / anchors.length, sumN / anchors.length, 0)
  return baseENUToLLA(center, parts[0].transform)
}

/**
 * 给锚点叠加人工校准偏移，再换算成 WGS84 经纬度。
 * @param {{enu:THREE.Vector3, transform:THREE.Matrix4}} anchor loadCityTiles 返回的锚点
 * @param {{east?:number, north?:number}} [calib] 偏移量（米），正值让模型整体向东/向北移动
 * @returns {{lat:number, lon:number, alt:number}}
 */
export function anchorLLA(anchor, calib) {
  const e = anchor.enu
  const p = new THREE.Vector3(e.x + ((calib && calib.east) || 0), e.y + ((calib && calib.north) || 0), e.z)
  return baseENUToLLA(p, anchor.transform)
}

/**
 * 圆形电子围栏：以「建筑模型群原点」为圆心，圆周密采样成多边形返回。
 * - 圆心 = 三维场景 (0,0,0) 对应的地理位置（即模型包围盒中心）+ 人工微调偏移 ——
 *   与模型的实际摆放位置一致，微调多少围栏就跟多少（"以微调偏移距离为基准"）。
 * - 半径 = 圆心到模型占地包围盒最远角点的水平距离 + margin，保证整圆盖住模型群并留缓冲。
 * 用途：高德楼块「指定范围隐藏」——圆内高德白模不显示（项目自有模型区域），
 * 圆外周边楼块照常显示。纯元数据计算，不加载 GLB，建图前就能调。
 * 注：高德 styleOpts.areas 只支持多边形围栏，故用 segments 个采样点逼近圆（默认 60，视觉上即圆）。
 * @param {{east?:number, north?:number}} [calib] 人工微调偏移（米），与 anchorLLA 的 calib 同约定
 * @param {{margin?:number, segments?:number}} [opts] margin=圆半径相对模型占地的外扩边距（米，默认 50）
 * @returns {{centerLLA:{lat:number,lon:number}, radius:number, path:Array<{lat:number,lon:number}>}}
 */
export function resolveModelFenceCircleLLA(calib, { margin = 50, segments = 60 } = {}) {
  const parts = collectParts()
  const baseInv = parts[0].transform.clone().invert()
  const box = new THREE.Box3()
  parts.forEach((part) => {
    partBoxCornersInBaseENU(part, baseInv).forEach((v) => box.expandByPoint(v))
  })
  if (box.isEmpty()) throw new Error('tileset 缺少 boundingVolume.box，无法解算模型占地范围')
  const m = Number.isFinite(margin) ? margin : 50
  const dE = (calib && calib.east) || 0
  const dN = (calib && calib.north) || 0
  // 圆心 = 模型群原点（包围盒中心）+ 微调偏移（与 anchorLLA 同约定：基准 ENU 坐标上直接相加）
  const c = box.getCenter(new THREE.Vector3())
  const cE = c.x + dE
  const cN = c.y + dN
  // 半径 = 圆心到占地包围盒最远角点的水平距离 + 外扩边距
  const radius = 0.5 * Math.hypot(box.max.x - box.min.x, box.max.y - box.min.y) + m
  const seg = Math.max(8, Number.isFinite(segments) ? Math.round(segments) : 60)
  const path = []
  for (let i = 0; i < seg; i++) {
    const a = (i / seg) * Math.PI * 2
    path.push(
      baseENUToLLA(
        new THREE.Vector3(cE + radius * Math.cos(a), cN + radius * Math.sin(a), 0),
        parts[0].transform,
      ),
    )
  }
  return {
    centerLLA: baseENUToLLA(new THREE.Vector3(cE, cN, 0), parts[0].transform),
    radius,
    path,
  }
}

// ============ 实测楼体占地（供逐楼围栏用） ============

/**
 * 拆分网格时按真实几何量出的每栋楼水平占地包围盒：
 * key = `${分片目录}|${楼号}`，value = { minX, maxX, minY, maxY }（分片局部 ENU，原始米，未乘微调缩放）。
 * 同一栋楼的几何可能分散在同一分片的多个 GLB 里，这里只累积包围盒，读的时候再算半径。
 * 用途：逐楼围栏的圆半径优先取实测值，替代「包围球×0.75」的粗略估算 ——
 * 包围球把楼群和高度跨度都包了进去（实测有 R>300m 的），估出来的圆远大于楼体、
 * 会铺满楼与楼之间的空地；实测值让圆真正贴合楼体轮廓。
 */
const MEASURED_FOOTPRINT = new Map()

/** 把一块几何的水平包围盒并进某栋楼的累积包围盒 */
function mergeMeasuredFootprint(key, box) {
  if (!box) return
  const cur = MEASURED_FOOTPRINT.get(key)
  if (!cur) {
    MEASURED_FOOTPRINT.set(key, { ...box })
    return
  }
  cur.minX = Math.min(cur.minX, box.minX)
  cur.maxX = Math.max(cur.maxX, box.maxX)
  cur.minY = Math.min(cur.minY, box.minY)
  cur.maxY = Math.max(cur.maxY, box.maxY)
}

/**
 * 逐楼圆形围栏：每个项目建筑（buildingNames 里有名称的楼，与标牌同源）各自一个圆。
 * - 圆心 = 该楼包围球球心，按 createTransformControls 同样的合成顺序叠加微调后的实际位置：
 *     先单栋楼（绕自身球心缩放 + 平移），再分片（绕分片包围盒中心缩放 + 平移），
 *     最后加整体锚点偏移（calib）——三层微调挪多少，圆心跟多少；
 *   即 X = 分片中心 + S分片·(球心 + 单栋偏移 − 分片中心) + 分片偏移 + calib偏移。
 * - 半径 = 楼体水平占地 × 各级缩放 + buffer。占地优先取「顶点实测」（拆分网格时按真实几何
 *   量出，见 MEASURED_FOOTPRINT，圆贴合楼体、不铺空地）；模型加载前无实测值时回退
 *   buildingRipple 的 rippleRadiusFromSphere（包围球×0.75 估算，限制在
 *   [minFootprint, maxFootprint] 米）。模型加载完成后调用方需重算一次（sceneView 已做）。
 * - 跨分片重复导出的同一栋楼（如 test 分片与 part1 重复的 9 / 20 号）只保留一个圆。
 * 建图前只有包围球估算可用（纯元数据、不加载 GLB）；scenetree 缺失时返回空数组（调用方自行回退）。
 * 注：高德 styleOpts.areas 只支持多边形，圆用 segments 个采样点逼近（默认 16，城市级缩放足够圆）。
 * @param {{east?:number, north?:number}} [calib] 整体锚点偏移（米），与 anchorLLA 的 calib 同约定
 * @param {Object} [partTransforms] 分片微调 { 分片目录: { east, north, up, scale, visible } }
 * @param {Object} [buildingTransforms] 单栋楼微调 { '分片目录|楼号': { east, north, up, scale, visible } }
 * @param {{buffer?:number, segments?:number, minFootprint?:number, maxFootprint?:number}} [opts]
 *        buffer=楼体占地外扩缓冲（米，默认 30）
 * @returns {Array<{key:string, code:string, name:string|null,
 *                  centerLLA:{lat:number,lon:number}, radius:number,
 *                  path:Array<{lat:number,lon:number}>}>}
 */
export function resolveBuildingFenceCirclesLLA(
  calib,
  partTransforms = null,
  buildingTransforms = null,
  { buffer = 30, segments = 16, minFootprint = 8, maxFootprint = 400 } = {},
) {
  const parts = collectParts()
  if (!parts.length) return []
  const baseInv = parts[0].transform.clone().invert()
  const dE = (calib && calib.east) || 0
  const dN = (calib && calib.north) || 0
  const seg = Math.max(8, Number.isFinite(segments) ? Math.round(segments) : 16)
  const buf = Number.isFinite(buffer) ? buffer : 30
  const circles = []
  const seen = [] // 已收楼心（基准 ENU，含微调），跨分片去重用
  parts.forEach((part) => {
    if (!part.tree) return
    // 分片微调（key = 分片目录；visible:false 或 scale:0 的分片不渲染，也无需隐藏区）
    const tp = Object.assign(
      { east: 0, north: 0, up: 0, scale: 1, visible: true },
      (partTransforms && partTransforms[part.dir]) || null,
    )
    if (tp.visible === false) return
    const sp = Math.max(0, Number(tp.scale) || 1)
    if (sp === 0) return
    // 分片包围盒中心（基准 ENU）—— 分片缩放的基准点，与 createTransformControls 一致
    const box = new THREE.Box3()
    partBoxCornersInBaseENU(part, baseInv).forEach((v) => box.expandByPoint(v))
    const cP = box.isEmpty() ? null : box.getCenter(new THREE.Vector3())
    const walk = (node) => {
      if (!node) return
      const code = node.name
      if (
        node.type === 'element' &&
        code &&
        Array.isArray(node.sphere) &&
        node.sphere.length >= 4 &&
        // 只给「项目建筑」生成隐藏区（与标牌同源：buildingNames 里查得到名称的 element）。
        // 周边白模/区片 tileset 的 Mesh_N 是按网格切块的 chunk，包围球会盖住大片空地，
        // 给它们画圆就会在"没有建筑模型的地方"铺满围栏 —— 明确跳过。
        buildingDisplayName(code)
      ) {
        const key = `${part.dir}|${code}`
        // 单栋楼微调：key 优先 `${分片目录}|${楼号}`，兼容只写楼号的写法
        const tbRaw = (buildingTransforms && (buildingTransforms[key] || buildingTransforms[code])) || null
        const tb = Object.assign({ east: 0, north: 0, up: 0, scale: 1, visible: true }, tbRaw)
        if (tb.visible !== false && Number(tb.scale) !== 0) {
          const sb = Math.max(0, Number(tb.scale) || 1)
          const p0 = sphereToBaseENU(node.sphere, baseInv) // 基准 ENU 楼心（微调前）
          // 分片局部 ENU 轴与基准 ENU 轴平行（仅原点平移），单栋偏移直接叠加
          let xe = p0.x + (tb.east || 0)
          let yn = p0.y + (tb.north || 0)
          if (cP) {
            xe = cP.x + sp * (xe - cP.x)
            yn = cP.y + sp * (yn - cP.y)
          }
          xe += (tp.east || 0) + dE
          yn += (tp.north || 0) + dN
          // 跨分片重复导出：同一楼号相距 50 米内，或任意楼心相距 5 米内，视为同一栋
          const dist = (s) => Math.hypot(s.x - xe, s.y - yn)
          const dup = seen.some((s) => (s.code === code && dist(s) < 50) || dist(s) < 5)
          if (!dup) {
            seen.push({ code, x: xe, y: yn })
            // 楼体水平占地：优先用拆分网格时按顶点实测的真实占地（贴合楼体，不会铺到空地）；
            // 模型还没加载（无实测值）时回退「包围球×0.75」估算（rippleRadiusFromSphere，
            // 与标牌光圈同约定）。包围球把楼群和高度跨度都包了进去，估算值普遍偏大。
            const fp = MEASURED_FOOTPRINT.get(key)
            const footprint = fp
              ? Math.min(
                Math.max(
                  0.5 * Math.hypot(fp.maxX - fp.minX, fp.maxY - fp.minY) * sb * sp,
                  minFootprint,
                ),
                maxFootprint,
              )
              : rippleRadiusFromSphere(node.sphere[3] * sb * sp, {
                min: minFootprint,
                max: maxFootprint,
              })
            const radius = footprint + buf
            const path = []
            for (let i = 0; i < seg; i++) {
              const a = (i / seg) * Math.PI * 2
              path.push(
                baseENUToLLA(
                  new THREE.Vector3(xe + radius * Math.cos(a), yn + radius * Math.sin(a), 0),
                  parts[0].transform,
                ),
              )
            }
            circles.push({
              key,
              code: String(code),
              name: buildingDisplayName(code) || null,
              centerLLA: baseENUToLLA(new THREE.Vector3(xe, yn, 0), parts[0].transform),
              radius,
              path,
              measured: !!fp, // 半径来源：true=顶点实测占地，false=包围球估算
            })
          }
        }
      }
      const kids = node.children || []
      kids.forEach(walk)
    }
    const scenes = part.tree.scenes || []
    scenes.forEach(walk)
  })
  return circles
}

// ============ 建筑名称标牌 ============

// 标牌底边（三角尖）相对 element 包围球球心的抬高量（米）。
// ⚠️ 锚点是「包围球球心」，大致落在建筑**中部**，不是楼顶。所以这个抬高量必须
// 足够大才能把牌子顶出楼体轮廓 —— 给太小（试过 24m）牌子正好卡在楼腰上，
// 被外立面遮掉一大半，看着就是"被挡住"。
// 另外这里用的是「球心 + 固定抬高」而不是「球心 + 半径」：
// 半径含水平跨度（有的楼半径上百米），加上去牌子会飘到天上。
// 55m ≈ 从楼腰再往上、基本越过常见楼顶（模型最高约 102m）。
export const LABEL_LIFT = 55

/**
 * 标牌与建筑之间的竖直连接线（引导线）样式。
 * 牌子悬在楼体上空，光靠位置对应有时分不清"牌子说的是哪栋楼"，
 * 加一条「三角尖 → 楼底」的细线把两者串起来，关系一目了然。
 *   · 颜色沿用标牌底沿主色（accentTop），和牌子连成一体；
 *   · 屏显宽度恒定（px），与标牌「恒定屏显大小」同一策略 —— 每帧按相机距离
 *     把像素宽换算成世界宽（换算在 sceneView.updateLabelScales 里做，k 值同源）；
 *   · 整体半透明 + 下端渐隐：线只是辅助元素不抢戏，楼体也盖得住它
 *     （见 makeLeaderLine：开深度测试，被楼体遮挡的部分不显示）。
 */
export const LEADER_LINE_STYLE = {
  color: '#153f5e', // 与 LABEL_STYLE.cardStroke 同色系（更亮一档，细线才看得见）
  widthPx: 2, // 屏显宽度（像素）
  opacityTop: 0.55, // 标牌一端（上），半透明避免突兀
  opacityBottom: 0.05, // 楼底一端（下），淡出"扎进"楼体
}

/**
 * 标牌样式参数（改样式只动这一块）。
 *
 * 设计：浅蓝半透明卡 + 深藏青字 + 实线蓝边 + 左侧楼号徽标 + 底沿主色条 + 下方三角尖。
 *   · 浅蓝在黄昏蓝调场景里既醒目又不会像白牌一样炸眼，半透明让楼体轮廓仍能透出来；
 *   · 实线深蓝边把卡片从任何底色上"切"出来，远景缩小时边界也不糊；
 *   · 楼号徽标保留业务标识作用（和面板里的楼号、buildingNames 的键一致）；
 *   · 徽标里的数字与名称文字都按「字形实际边界」垂直居中：middle 基线按 em 框取中，
 *     对汉字并不可靠，之前固定上移 4% 的光学补偿在微软雅黑下反而偏上。
 */
const LABEL_STYLE = {
  // 卡片主体（浅蓝渐变 + 半透明；上浅下深一点，让卡片有轻微体积感）
  // ⚠️ 这里的色值要按「ACES + 曝光 2.45 之后的输出」来配，不能照屏幕观感直接填浅蓝：
  // 开后期时 SpriteMaterial.toneMapped 是空转的（见文件下方 sprite 注释），
  // 旧的 #ecf7ff 实测输出 RGB(240,242,244)，已经读成白色。压到 #a5d2f0 这一档，
  // 输出才是用户要的浅蓝；再浅就又被推到白。
  cardTop: 'rgba(165, 210, 240, 0.92)',
  cardMid: 'rgba(135, 189, 227, 0.88)',
  cardBottom: 'rgba(105, 165, 211, 0.86)',
  cardStroke: '#0a2a44',
  cardStrokeW: 8, // 画布像素；缩到屏显 46px 高时约 1.9px，是一条清楚的实线
  radiusRatio: 0.22, // 圆角半径 = 卡片高 * 该比例

  // 底沿主色条（同时作为下方三角尖、楼号徽标的颜色）
  accentTop: '#1e5f96',
  accentBottom: '#123f68',
  accentH: 0.14, // 底沿高度 = 卡片高 * 该比例

  // 楼号徽标
  badgeW: 1.0, // 徽标边长 = 字号 * 该比例
  badgeGap: 0.34,
  badgeText: '#ffffff',

  // 名称文字（深藏青；同样按后期输出配，保证在浅蓝底上读起来是"深色"）
  textColor: '#071626',
  fontSize: 80, // 画布字号：屏幕显示大小由 sceneView 的 LABEL_SCREEN_HEIGHT_PX 决定，
  //               调大它只提升纹理清晰度，不改变屏显大小
  maxChars: 10, // 超过这个字数等比缩字号
  charGap: 0.5, // 中文逐字间距 = 字号 * 该比例（仅多字时生效）
  maxTextEm: 6.2, // 文字最大宽 = 字号 * 该倍数，超出继续缩字号
  textGap: 0.3, // 文字距徽标的间距

  // 内边距（相对字号）。压薄上下留白，让字号在同样面板高度里占得更满。
  padX: 0.56,
  padY: 0.24,

  // 尾部三角尖（短一些，牌子离楼更近）
  tailHRatio: 0.18, // 尖端高 = 卡片高 * 该比例
  tailWRatio: 0.28, // 尖端底宽 = 卡片高 * 该比例

  // 阴影偏移（canvas 里没有 CSS 的 box-shadow，只能自己画一圈偏置的深色轮廓）
  shadowX: 0,
  shadowY: 6,
  shadowBlur: 12,
  shadowColor: 'rgba(8, 28, 48, 0.32)',
}

/** 圆角矩形路径（兼容不支持 ctx.roundRect 的旧浏览器） */
function roundRectPath(ctx, x, y, w, h, r) {
  const rr = Math.min(r, w / 2, h / 2)
  ctx.beginPath()
  ctx.moveTo(x + rr, y)
  ctx.lineTo(x + w - rr, y)
  ctx.quadraticCurveTo(x + w, y, x + w, y + rr)
  ctx.lineTo(x + w, y + h - rr)
  ctx.quadraticCurveTo(x + w, y + h, x + w - rr, y + h)
  ctx.lineTo(x + rr, y + h)
  ctx.quadraticCurveTo(x, y + h, x, y + h - rr)
  ctx.lineTo(x, y + rr)
  ctx.quadraticCurveTo(x, y, x + rr, y)
  ctx.closePath()
}

/**
 * 逐字绘制名称并手动加字间距。
 * 不用 ctx.letterSpacing：Chromium 99 才支持，本项目 serve 环境不一定有，
 * 手写循环最稳，顺便能拿到准确的文字宽度用来算卡片尺寸。
 *
 * 垂直居中：不依赖 textBaseline=middle（它按 em 框取中，对汉字不可靠），
 * 而是测出整行字形的实际上/下边界，把基线反推出来，让「字形视觉中心」正好落在 cy。
 * 行内取所有字形的最大上/下边界，混排拉丁字符时整行仍以汉字主体对齐、不会高低不齐；
 * 老浏览器没有 actualBoundingBox* 时退回 middle 基线。
 * @returns {number} 文字总宽度
 */
function drawSpacedText(ctx, text, cx, cy, fontPx, letterSpacing) {
  const chars = [...text]
  const widths = chars.map((c) => ctx.measureText(c).width)
  const total = widths.reduce((s, w) => s + w, 0) + letterSpacing * Math.max(0, chars.length - 1)
  ctx.textAlign = 'left'
  let baselineY = cy
  if (chars.length) {
    let maxAsc = 0
    let maxDesc = 0
    let hasBox = true
    for (const c of chars) {
      const m = ctx.measureText(c)
      if (m.actualBoundingBoxAscent === undefined) {
        hasBox = false
        break
      }
      maxAsc = Math.max(maxAsc, m.actualBoundingBoxAscent)
      maxDesc = Math.max(maxDesc, m.actualBoundingBoxDescent)
    }
    if (hasBox) {
      ctx.textBaseline = 'alphabetic'
      baselineY = cy + (maxAsc - maxDesc) / 2
    } else {
      ctx.textBaseline = 'middle' // 兑底：老浏览器拿不到字形边界
    }
  }
  let x = cx - total / 2
  chars.forEach((c, i) => {
    ctx.fillText(c, x, baselineY)
    x += widths[i] + letterSpacing
  })
  return total
}

/**
 * 用 canvas 画一块标牌：浅蓝半透明卡 + 左侧楼号徽标 + 深藏青名称 + 底沿主色条 + 下方三角尖。
 *
 * 尺寸策略：卡片"高度"是稳定的（由字号 + 内边距决定），宽度随楼名长度增长，
 * 所以屏幕上 21 块牌子的高度完全一致、长短不齐 —— 这正是想要的观感。
 * （sceneView 每帧只保证 sprite 的"高度"为固定像素数，宽度按 aspect 自动展开。）
 *
 * @param {string} text 建筑名称
 * @param {string} code 楼号（画进徽标）
 * @returns {{texture:THREE.Texture, aspect:number}}
 */
function makeLabelTexture(text, code) {
  const S = LABEL_STYLE
  const canvas = document.createElement('canvas')
  let ctx = canvas.getContext('2d')

  // ---- 定字号：名字太长就逐级缩，保证牌子不会被拉得又长又飘 ----
  const chars = [...String(text)]
  let fontPx = S.fontSize * Math.min(1, S.maxChars / Math.max(1, chars.length))
  let letterSpacing = chars.length > 1 ? fontPx * S.charGap : 0

  const fontOf = (px) => `700 ${Math.round(px)}px "Microsoft YaHei", "PingFang SC", sans-serif`
  ctx.font = fontOf(fontPx)
  let textW = chars.reduce((s, c) => s + ctx.measureText(c).width, 0) + letterSpacing * Math.max(0, chars.length - 1)
  // 再按"最大文字宽度"兜一次底（极长名称）
  const maxTextW = fontPx * S.maxTextEm
  if (textW > maxTextW) {
    const k = maxTextW / textW
    fontPx *= k
    letterSpacing *= k
    ctx.font = fontOf(fontPx)
    textW =
      chars.reduce((s, c) => s + ctx.measureText(c).width, 0) + letterSpacing * Math.max(0, chars.length - 1)
  }
  // 徽标里最多塞 2 个字符（"10-1" 这种），字号按位数略缩
  const badgeChars = [...String(code || '')]
  const badgeFontPx = fontPx * (badgeChars.length >= 3 ? 0.6 : badgeChars.length === 2 ? 0.72 : 0.9)

  // ---- 量尺寸（padX/padY 按字号换算，字号一改整体等比缩放）----
  const padX = fontPx * S.padX
  const padY = fontPx * S.padY
  const badgeW = fontPx * S.badgeW
  const plateH = fontPx * 1.17 + padY * 2
  const accentH = Math.max(2, plateH * S.accentH)
  const plateW = padX * 2 + badgeW + fontPx * S.badgeGap + textW
  const tailH = plateH * S.tailHRatio
  const tailW = plateH * S.tailWRatio
  const pad = Math.max(6, Math.round(fontPx * 0.24)) // 给阴影留的边距

  canvas.width = Math.round(plateW + pad * 2)
  canvas.height = Math.round(plateH + tailH + pad * 2)
  ctx = canvas.getContext('2d')

  // 卡片 + 三角尖的外轮廓（同一条路径，方便整块投影与描边）
  const cx = pad + plateW / 2
  const cardTop = pad
  const cardBottom = pad + plateH
  const r = plateH * S.radiusRatio
  const tailHLen = tailH
  const tailWLen = tailW

  const fullOutline = () => {
    ctx.beginPath()
    ctx.moveTo(pad + r, cardTop)
    ctx.lineTo(pad + plateW - r, cardTop)
    ctx.quadraticCurveTo(pad + plateW, cardTop, pad + plateW, cardTop + r)
    ctx.lineTo(pad + plateW, cardBottom - r)
    ctx.quadraticCurveTo(pad + plateW, cardBottom, pad + plateW - r, cardBottom)
    ctx.lineTo(cx + tailWLen / 2, cardBottom)
    ctx.lineTo(cx, cardBottom + tailHLen)
    ctx.lineTo(cx - tailWLen / 2, cardBottom)
    ctx.lineTo(pad + r, cardBottom)
    ctx.quadraticCurveTo(pad, cardBottom, pad, cardBottom - r)
    ctx.lineTo(pad, cardTop + r)
    ctx.quadraticCurveTo(pad, cardTop, pad + r, cardTop)
    ctx.closePath()
  }

  // ---- 1) 投影：整块轮廓略微下移、模糊描一遍 ----
  ctx.save()
  ctx.shadowColor = S.shadowColor
  ctx.shadowBlur = S.shadowBlur
  ctx.shadowOffsetX = S.shadowX
  ctx.shadowOffsetY = S.shadowY
  // 用多次描边模拟柔和投影（canvas 的阴影对填充路径更明显）
  fullOutline()
  ctx.fillStyle = 'rgba(16, 42, 76, 0.001)'
  ctx.fill()
  ctx.restore()

  // ---- 2) 卡片主体（浅蓝渐变，半透明） ----
  fullOutline()
  const cardGrad = ctx.createLinearGradient(0, cardTop, 0, cardBottom)
  cardGrad.addColorStop(0, S.cardTop)
  cardGrad.addColorStop(0.55, S.cardMid)
  cardGrad.addColorStop(1, S.cardBottom)
  ctx.fillStyle = cardGrad
  ctx.fill()

  // ---- 3) 底沿主色条（裁到整块卡片内，不越过圆角） ----
  // ⚠️ 裁剪区必须用 fullOutline（底边在卡片下沿）；之前那条内容区路径停在主色条上沿，
  // 拿它当裁剪区等于把要画的那一条全裁掉，主色条一直没显示出来。
  ctx.save()
  fullOutline()
  ctx.clip()
  const acc = ctx.createLinearGradient(0, cardBottom - accentH, 0, cardBottom)
  acc.addColorStop(0, S.accentTop)
  acc.addColorStop(1, S.accentBottom)
  ctx.fillStyle = acc
  ctx.fillRect(pad, cardBottom - accentH, plateW, accentH)
  ctx.restore()

  // ---- 4) 三角尖填主色（和底沿同色，视觉上连成一条"指向"） ----
  ctx.beginPath()
  ctx.moveTo(cx - tailWLen / 2, cardBottom - 1)
  ctx.lineTo(cx, cardBottom + tailHLen)
  ctx.lineTo(cx + tailWLen / 2, cardBottom - 1)
  ctx.closePath()
  ctx.fillStyle = S.accentBottom
  ctx.fill()

  // ---- 5) 整块描边（实线勾住轮廓，把卡片从底图上"切"出来） ----
  fullOutline()
  ctx.lineWidth = S.cardStrokeW
  ctx.strokeStyle = S.cardStroke
  ctx.stroke()

  // 内容的统一视觉中线：卡片去掉底沿主色条之后所剩区域的中点。
  // 徽标与文字都以它为中心，两者才会真正对齐
  //（之前徽标按整张卡片居中、文字又额外上移了 accentH/2，两边各偏一点，看着就是"没对齐"）。
  const contentCy = cardTop + (plateH - accentH) / 2

  // ---- 6) 楼号徽标（主色渐变方块，白字；数字按字形实际边界在方块内垂直居中） ----
  const bx = pad + padX
  const by = contentCy - badgeW / 2
  const bGrad = ctx.createLinearGradient(bx, by, bx + badgeW, by + badgeW)
  bGrad.addColorStop(0, S.accentTop)
  bGrad.addColorStop(1, S.accentBottom)
  roundRectPath(ctx, bx, by, badgeW, badgeW, badgeW * 0.28)
  ctx.fillStyle = bGrad
  ctx.fill()
  ctx.font = `700 ${Math.round(badgeFontPx)}px "Microsoft YaHei", "PingFang SC", sans-serif`
  ctx.textAlign = 'center'
  ctx.fillStyle = S.badgeText
  const badgeTextStr = badgeChars.join('')
  const bm = ctx.measureText(badgeTextStr)
  if (bm.actualBoundingBoxAscent !== undefined) {
    // 实测字形边界反推基线：数字的「视觉中心」正好落在徽标几何中心
    ctx.textBaseline = 'alphabetic'
    ctx.fillText(
      badgeTextStr,
      bx + badgeW / 2,
      by + badgeW / 2 + (bm.actualBoundingBoxAscent - bm.actualBoundingBoxDescent) / 2,
    )
  } else {
    // 兑底：老浏览器拿不到字形边界，退回 middle 基线
    ctx.textBaseline = 'middle'
    ctx.fillText(badgeTextStr, bx + badgeW / 2, by + badgeW / 2)
  }

  // ---- 7) 名称文字（深藏青；drawSpacedText 内部按字形边界垂直居中到 contentCy） ----
  const textCx = pad + padX + badgeW + fontPx * S.badgeGap + textW / 2
  ctx.font = fontOf(fontPx)
  ctx.fillStyle = S.textColor
  drawSpacedText(ctx, String(text), textCx, contentCy, fontPx, letterSpacing)

  const texture = new THREE.CanvasTexture(canvas)
  texture.colorSpace = THREE.SRGBColorSpace
  texture.needsUpdate = true
  return { texture, aspect: canvas.width / canvas.height }
}

/**
 * 楼号徽标（单独一块方牌）：名称牌被屏幕避让收起来时顶上，保证"这栋楼是几号"永远查得到。
 * 画法和 makeLabelTexture 里那块内嵌徽标完全一致（同一套渐变、同一个圆角比例），
 * 所以"收起名称牌、露出徽标"不会让人觉出是两种东西。
 */
function makeBadgeTexture(code) {
  const S = LABEL_STYLE
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')
  const F = S.fontSize // 和名称牌同一清晰度基准：屏显大小由 sceneView 决定，这里只管清晰度
  const side = F * 1.35
  const pad = Math.round(F * 0.3)
  canvas.width = canvas.height = Math.round(side + pad * 2)
  const c = canvas.width / 2
  const r = side / 2

  ctx.save()
  ctx.shadowColor = S.shadowColor
  ctx.shadowBlur = S.shadowBlur
  ctx.shadowOffsetY = S.shadowY
  const grad = ctx.createLinearGradient(c - r, c - r, c + r, c + r)
  grad.addColorStop(0, S.accentTop)
  grad.addColorStop(1, S.accentBottom)
  roundRectPath(ctx, c - r, c - r, side, side, side * 0.28)
  ctx.fillStyle = grad
  ctx.fill()
  ctx.restore()

  ctx.lineWidth = S.cardStrokeW * 1.6
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.72)'
  roundRectPath(ctx, c - r + ctx.lineWidth * 0.4, c - r + ctx.lineWidth * 0.4, side - ctx.lineWidth * 0.8, side - ctx.lineWidth * 0.8, side * 0.26)
  ctx.stroke()

  const chars = [...String(code || '')]
  const fontPx = F * (chars.length >= 3 ? 0.62 : chars.length === 2 ? 0.78 : 0.95)
  ctx.font = `700 ${Math.round(fontPx)}px "Microsoft YaHei", "PingFang SC", sans-serif`
  ctx.textAlign = 'center'
  ctx.fillStyle = S.badgeText
  const str = chars.join('')
  const m = ctx.measureText(str)
  if (m.actualBoundingBoxAscent !== undefined) {
    ctx.textBaseline = 'alphabetic'
    ctx.fillText(str, c, c + (m.actualBoundingBoxAscent - m.actualBoundingBoxDescent) / 2)
  } else {
    ctx.textBaseline = 'middle'
    ctx.fillText(str, c, c)
  }

  const texture = new THREE.CanvasTexture(canvas)
  texture.colorSpace = THREE.SRGBColorSpace
  texture.needsUpdate = true
  return { texture, aspect: 1 }
}

/** scenetree 里的 sphere 是 ECEF 球心 [x,y,z,radius]，换算到基准分片 ENU */
function sphereToBaseENU(sphere, baseInv) {
  return new THREE.Vector3(sphere[0], sphere[1], sphere[2]).applyMatrix4(baseInv)
}

/**
 * 汇总所有分片 scenetree 里 type=element 的节点，用楼号去 buildingNames 表换显示名称。
 * 规则（按需求）：**没有对应名称的一律不生成标牌** ——
 *   · 周边白模的节点名是 Mesh_N（无业务含义），过滤掉；
 *   · 楼号在 buildingNames 里查不到的（如 3 / 7 / 8 / 16 / 17 号楼的几何当前缺失），也过滤掉。
 * 去重按「名称 + 位置」：同名且相距 50 米以内视为同一栋楼的重复导出（如 test 分片里的 9 / 20
 * 与 part1 只差 15 米），只保留第一个；同名但位置明显不同（如 part1 的 "10-1" 与 part2 的 "10"
 * 相隔 760 米）则各留一块，保证标牌就落在它自己的几何上方。
 * 注意：sphere 是「包围球」→ 球心大致在建筑中部、半径含水平跨度，
 * 所以标牌高度用「球心 + 固定抬高量」，不要用「球心 + 半径」（会飘到天上）。
 * @returns {Array<{code:string, name:string, enu:THREE.Vector3, radius:number, part:string}>}
 */
const LABEL_DEDUPE_DISTANCE = 50 // 米

function collectLabelAnchors(parts, baseInv) {
  const out = []
  const unmapped = new Set()
  parts.forEach((part) => {
    if (!part.tree) return
    const partInv = part.transform.clone().invert()
    const walk = (node) => {
      if (!node) return
      const code = node.name
      if (node.type === 'element' && code && Array.isArray(node.sphere)) {
        const name = buildingDisplayName(code)
        if (!name) {
          if (!/^Mesh_\d+$/.test(code)) unmapped.add(code)
        } else {
          const enu = sphereToBaseENU(node.sphere, baseInv)
          const dup = out.find((o) => o.name === name && o.enu.distanceTo(enu) < LABEL_DEDUPE_DISTANCE)
          if (dup) {
            console.info(
              `[loadCityTiles] "${name}" 在 ${part.dir} 又出现一次且位置几乎重合，判定为重复分片，跳过`,
            )
          } else {
            out.push({
              code: String(code),
              name,
              enu,
              // 分片局部 ENU（Z-up）：标牌/光圈要跟着该分片的微调一起动，所以按局部坐标存
              local: sphereToBaseENU(node.sphere, partInv),
              radius: node.sphere[3],
              part: part.dir,
            })
          }
        }
      }
      const kids = node.children || []
      kids.forEach(walk)
    }
    const scenes = part.tree.scenes || []
    scenes.forEach(walk)
  })
  if (unmapped.size) {
    console.info(
      `[loadCityTiles] 以下楼号在 buildingNames.js 里没有对应名称，已跳过标牌：${[...unmapped].join('、')}`,
    )
  }
  return out
}


/**
 * 「寄宿标牌」：楼号在 scenetree 里没有自己的 element（几何并进了别的楼的分片），
 * 自动流程不会为它生成标牌，这里按「宿主楼锚点 + 偏移」手动补一块：
 *   · code：补出来的标牌显示的楼号（名称取 buildingNames 表）；
 *   · host：宿主楼的楼号 —— 标牌挂在宿主标牌锚点旁边，随宿主一起走（分片微调时同步移动）；
 *   · offset：相对宿主标牌锚点的水平/竖直偏移（米，基准 ENU 方向，east=东 north=北 up=上）。
 * 一旦该楼号补上了独立几何（scenetree 出现同名 element），自动标牌会接管，
 * 这里的补牌因「楼号已存在」自动跳过，不用回来删配置。
 */
const EXTRA_LABELS = [
  {
    // 4 号楼（旺田·精品女装市场）与 12 号楼（中洲女装城）模型连成一体，
    // 几何全在 20260913_12 分片的一块合并网格里，scenetree 里查不到 element "4"。
    // offset 依据：真实 POI 里旺田在中洲女装城南偏西约 44m（东 -14.5、北 -41.9，
    // 高德 PlaceSearch 杭海路88号 vs 杭海路92号）；12 号标牌悬在合并体几何中心
    // （≈两楼中点），故取约半程偏南，同时保证两块牌子拉开间距不重叠。
    code: '4',
    host: '12',
    offset: { east: -20, north: -20 },
  },
  {
    code: '3',
    host: '13',
    offset: { east: -20, north: -20 },
  },
]

/** 把 EXTRA_LABELS 追加进自动收集的标牌锚点；宿主缺失或楼号已有真实几何时跳过 */
function applyExtraLabels(anchors) {
  EXTRA_LABELS.forEach((cfg) => {
    if (anchors.some((a) => a.code === String(cfg.code))) return // 已有独立几何，自动标牌接管
    const name = buildingDisplayName(cfg.code)
    const host = anchors.find((a) => a.code === String(cfg.host))
    if (!name || !host) {
      console.warn(
        `[loadCityTiles] 补充标牌 ${cfg.code} 跳过：` +
        (!name ? 'buildingNames 里没有名称' : `找不到宿主楼 ${cfg.host}`),
      )
      return
    }
    const off = new THREE.Vector3(cfg.offset.east || 0, cfg.offset.north || 0, cfg.offset.up || 0)
    anchors.push({
      code: String(cfg.code),
      name,
      enu: host.enu.clone().add(off),
      // 分片局部 ENU 与基准 ENU 轴向一致（仅原点平移），偏移直接叠加
      local: host.local.clone().add(off),
      radius: host.radius,
      part: host.part,
    })
  })
  return anchors
}

/**
 * 标牌→建筑的竖直连接线：单位高开口圆柱（顶面在原点、向下展开），顶点色做上下渐隐。
 * 用圆柱而不是 THREE.Line：Line 的 linewidth 在 Windows ANGLE 下恒为 1px 且俯视时
 * 侧棱消失；圆柱从任意角度看都有厚度，俯视时退化成一个小圆点（物理上本来也该这样）。
 */
function makeLeaderLine() {
  const geo = new THREE.CylinderGeometry(0.5, 0.5, 1, 8, 1, true)
  geo.translate(0, -0.5, 0) // 顶面移到原点：放置时对准标牌三角尖，scale.y = 线长
  // 顶点色带 alpha：上端实、下端渐隐
  const posAttr = geo.attributes.position
  const colors = new Float32Array(posAttr.count * 4)
  const c = new THREE.Color(LEADER_LINE_STYLE.color)
  for (let i = 0; i < posAttr.count; i++) {
    const alpha = posAttr.getY(i) > -0.5 ? LEADER_LINE_STYLE.opacityTop : LEADER_LINE_STYLE.opacityBottom
    colors[i * 4] = c.r
    colors[i * 4 + 1] = c.g
    colors[i * 4 + 2] = c.b
    colors[i * 4 + 3] = alpha
  }
  geo.setAttribute('color', new THREE.BufferAttribute(colors, 4))
  const mesh = new THREE.Mesh(
    geo,
    new THREE.MeshBasicMaterial({
      vertexColors: true,
      transparent: true,
      depthWrite: false,
      // 正常参与深度测试：楼体在镜头与线之间时盖住线 —— 线是辅助元素，
      // 层级低于建筑模型（与标牌不同：标牌是信息层，depthTest 关掉永远压在楼上）
      toneMapped: false, // 不吃色调映射，和牌子颜色保持一致（关掉后期时才真正生效，见 sprite 处注释）
      side: THREE.DoubleSide, // 开口圆柱没有顶盖，俯视时看到的是内壁
    }),
  )
  mesh.name = 'labelLeaderLine'
  // renderOrder 保持默认 0：不做信息层压楼那种特殊排序，深度测试自然处理遮挡；
  // 标牌 renderOrder 999 仍然最后画，压在线上不受影响
  mesh.frustumCulled = false // scale 每帧在变，不做视锥剔除
  mesh.raycast = () => { } // 不参与点击拾取：点到线不应误触选中，点击只认牌子本身
  return mesh
}

/**
 * 生成标签组（挂到 cityGroup 下，自动继承居中偏移与 Y-up->Z-up 的坐标换算）。
 * cityGroup 局部坐标 = (东, 上, -北)，故基准 ENU 的 (E,N,U) -> 局部 (E, U + 抬高量, -N)。
 * 用 Sprite 而不是 CSS2D：不额外插 DOM，且能被楼体遮挡、随地图一起缩放。
 * 尺寸不在这里定死 —— 由 sceneView 每帧按相机距离重算，保证标牌在屏幕上大小恒定。
 */
function buildLabelGroup(anchors, baseTransform, partMatrixOf) {
  const group = new THREE.Group()
  group.name = 'buildingLabels'

  anchors.forEach((a) => {
    // 楼号一起交给贴图：新样式里楼号画在左侧徽标上（业务标识，和面板 / buildingNames 的键一致）
    const { texture, aspect } = makeLabelTexture(a.name, a.code)
    const sprite = new THREE.Sprite(
      new THREE.SpriteMaterial({
        map: texture,
        transparent: true,
        // 不写深度，且**不做深度测试** —— 标牌永远画在模型之上。
        // 之前保留 depthTest 时，牌子只要被楼体或周边白模挡到就整块消失/缺一半，
        // 表现就是"被挡住"。名称标牌属于信息层，不该参与三维遮挡。
        depthWrite: false,
        depthTest: false,
        toneMapped: false,
        // ℹ️ 开着后期（composer）时这个标记其实是空转：three 往 RenderPass 的
        // render target 里画的时候一律**跳过**材质色调映射（WebGLPrograms.getParameters
        // 里那句 currentRenderTarget === null 的判断），ACES 是最后 OutputPass 一次性做的。
        // 所以"牌子白到发光"不能靠改这里解决 —— Bloom 的 threshold 比的是**线性值**。
        // 保留 false 是为了 BLOOM_ENABLED=false 那条直出路径上牌子的颜色仍然干净。
      }),
    )
    sprite.center.set(0.5, 0) // 以三角尖为锚点，牌子向上展开
    sprite.name = 'label_' + a.code
    // 强制最后画：depthTest 关掉后仍受 renderOrder 影响，给个大值保证压在所有楼之上
    sprite.renderOrder = 999
    // 位置按"分片 ENU（东, 北, 上；Z 向上）"存，再由 relayoutLabels 经"分片矩阵 × rotX(-90)"
    // 换到场景坐标 (东, 上, -北) —— 这一步必须和模型内部 rootNode 的转换一致，否则标牌会和楼错位。
    sprite.userData = {
      labelKind: 'plate', // 名称牌（会被屏幕避让收起）；徽标是 'badge'，见下
      aspect,
      buildingCode: a.code,
      buildingName: a.name,
      part: a.part,
      localTop: new THREE.Vector3(a.local.x, a.local.y, a.local.z + LABEL_LIFT),
      // ⚠️ 地面点要落在"地面"上：a.local.z 是包围球心（楼中部）的标高，
      // 拿它当地面会让光圈卡在楼腰上、盖住建筑。这里直接用地面标高 1.5m（略高于地形）。
      localGround: new THREE.Vector3(a.local.x, a.local.y, 1.5),
      baseRadius: a.radius,
      cityPos: new THREE.Vector3(), // 场景（cityGroup 局部）坐标，relayout 时写入
      ground: new THREE.Vector3(),
      lla: null,
      sphereRadius: a.radius,
    }
    sprite.position.copy(sprite.userData.localTop).applyMatrix4(partMatrixOf(a.part))
    group.add(sprite)
    // 标牌→建筑的竖直连接线：挂在同一个 labelGroup 下（随标牌一起显隐），
    // 引用存在 userData.leader 上 —— relayoutLabels 改 cityPos/ground 后，
    // sceneView 每帧按这两个端点摆线，微调分片/单楼时线会自动跟着走
    const leader = makeLeaderLine()
    leader.position.copy(sprite.position)
    sprite.userData.leader = leader
    group.add(leader)

    // 楼号徽标：名称牌被避让掉时才露出来，位置正好是名称牌三角尖的尖端
    //（sprite.center 是 (0.5, 0)=底边中点，徽标 center 用 (0.5, 0.5)=正中，
    //  所以两者共用同一个 position 时，徽标恰好"填进"名称牌收起后留下的那个点）
    const badge = new THREE.Sprite(
      new THREE.SpriteMaterial({
        map: makeBadgeTexture(a.code).texture,
        transparent: true,
        depthWrite: false,
        depthTest: false,
        toneMapped: false, // 同名称牌：开后期时该标记空转，见上面 sprite 处那段说明
      }),
    )
    badge.center.set(0.5, 0.5)
    badge.name = 'badge_' + a.code
    badge.renderOrder = 998 // 压过所有楼体，但排在名称牌之下
    badge.userData = { labelKind: 'badge', aspect: 1, buildingCode: a.code, buildingName: a.name, part: a.part }
    badge.position.copy(sprite.position)
    sprite.userData.badge = badge
    badge.userData.plate = sprite
    group.add(badge)
  })
  return group
}

// ============ 单模型微调：按分片做位移 + 等比缩放 ============

/**
 * 一栋楼在世界坐标里的包围盒。
 *
 * ⚠️ 绝不能用 `Box3.expandByObject(mesh)`：拆出来的 subGeometry 与原合并网格
 * **共享同一条 position 属性**（只有 index 不同），而 `computeBoundingBox()` 扫的是
 * 整条缓冲、不是本楼那部分索引。一栋楼有 13~36 个 mesh，就是 13~36 次"整片分片顶点
 * 全扫"——实测第一次点中建筑要卡 3.0 秒。这里只遍历该 mesh 自己的索引，并且把结果
 * 按几何体缓存成**局部**盒（微调会改世界矩阵，局部盒不会变）。
 */
function meshWorldBox(mesh) {
  const geo = mesh.geometry
  const pos = geo && geo.attributes && geo.attributes.position
  if (!pos) return null
  if (geo.userData.fsgLocalBox === undefined) {
    const idx = geo.index
    const n = idx ? idx.count : pos.count
    let minX = Infinity
    let minY = Infinity
    let minZ = Infinity
    let maxX = -Infinity
    let maxY = -Infinity
    let maxZ = -Infinity
    const v = new THREE.Vector3()
    for (let i = 0; i < n; i++) {
      const k = idx ? idx.getX(i) : i
      v.fromBufferAttribute(pos, k)
      if (v.x < minX) minX = v.x
      if (v.x > maxX) maxX = v.x
      if (v.y < minY) minY = v.y
      if (v.y > maxY) maxY = v.y
      if (v.z < minZ) minZ = v.z
      if (v.z > maxZ) maxZ = v.z
    }
    geo.userData.fsgLocalBox =
      maxX >= minX ? new THREE.Box3(new THREE.Vector3(minX, minY, minZ), new THREE.Vector3(maxX, maxY, maxZ)) : null
  }
  const lb = geo.userData.fsgLocalBox
  if (!lb) return null
  return lb.clone().applyMatrix4(mesh.matrixWorld)
}

/** 楼号 → buildingMap 的键（同楼号理论上可能落在多个分片，取有几何的那个） */
function buildingKeyByCode(buildingMap, code) {
  const s = String(code)
  return Object.keys(buildingMap).find((k) => String(buildingMap[k].code) === s) || null
}

/** 分片/单栋楼微调参数，单位米 / 倍数。east/north/up 正值 = 向东/北/上移动；visible=false 隐藏该模型 */
export const DEFAULT_PART_TRANSFORM = { east: 0, north: 0, up: 0, scale: 1, visible: true }

// 分片 ENU（东,北,上）-> 场景（东,上,-北）：模型内部靠 GLB 的 rootNode 完成这步，
// 标牌是直接挂在场景下的，必须自己补上，否则标牌会和建筑错位。
const ROT_ENU_TO_SCENE = new THREE.Matrix4().makeRotationX(-Math.PI / 2)
const _m4 = new THREE.Matrix4()

function createTransformControls(partMap, buildingMap, labelGroup, baseTransform) {
  const partState = {} // 分片目录 -> { east, north, up, scale }
  const buildingState = {} // `${分片}|${楼号}` -> { east, north, up, scale }

  // 只数"名称牌"：徽标是它的附属（位置由 relayoutLabels 同步），不能在这里被当成第二栋楼，
  // 否则微调一次会把两栋楼的量算成四块牌子。
  const spritesOf = () =>
    labelGroup ? labelGroup.children.filter((o) => o.isSprite && o.userData.labelKind === 'plate') : []

  /**
   * 分片矩阵合成：先以分片自身包围盒中心为基准等比缩放，再整体平移到目标位置。
   *   M = T_geoOffset(父空间) · baseMatrix · T(center) · S(scale) · T(-center)
   * 偏移量用"父空间轴"表示，cityGroup 局部轴 = (东, 上, -北)，所以 up 直接是 +y。
   */
  function composePartMatrix(info, t) {
    const m = new THREE.Matrix4()
    const off = new THREE.Matrix4().makeTranslation(t.east, t.up, -t.north)
    const c = info.localCenter
    m.multiplyMatrices(off, info.baseMatrix)
    m.multiply(new THREE.Matrix4().makeTranslation(c.x, c.y, c.z))
    m.multiply(new THREE.Matrix4().makeScale(t.scale, t.scale, t.scale))
    m.multiply(new THREE.Matrix4().makeTranslation(-c.x, -c.y, -c.z))
    return m
  }

  /**
   * 单栋楼矩阵合成（分片 ENU 空间：x=东, y=北, z=上）。
   *   M = T(center + 偏移) · S(scale) · T(-center)
   * 缩放以该楼自身包围球心为基准。
   */
  function composeBuildingMatrix(info, t) {
    const c = info.enuCenter
    const m = new THREE.Matrix4()
    m.makeTranslation(c.x + t.east, c.y + t.north, c.z + t.up)
    m.multiply(new THREE.Matrix4().makeScale(t.scale, t.scale, t.scale))
    m.multiply(new THREE.Matrix4().makeTranslation(-c.x, -c.y, -c.z))
    return m
  }

  /** 标牌位置/地面点/经纬度全部按当前分片 + 该楼微调重算；不传参则全部重算 */
  function relayoutLabels(partDir, code) {
    spritesOf().forEach((sp) => {
      const ud = sp.userData
      if (partDir && ud.part !== partDir) return
      if (code && ud.buildingCode !== code) return
      const info = partMap[ud.part]
      if (!info) return
      const bInfo = buildingMap[`${ud.part}|${ud.buildingCode}`]
      const bState = buildingState[`${ud.part}|${ud.buildingCode}`]

      // 分片 ENU -> 场景 (东, 上, -北)：分片矩阵再乘 rotX(-90)，与模型内部 rootNode 的转换一致
      const toCity = _m4.multiplyMatrices(info.group.matrix, ROT_ENU_TO_SCENE)

      const p = ud.localTop.clone()
      if (bInfo && bState) p.applyMatrix4(composeBuildingMatrix(bInfo, bState))
      p.applyMatrix4(toCity)
      ud.cityPos.copy(p)
      sp.position.copy(ud.cityPos)
      if (ud.badge) ud.badge.position.copy(sp.position) // 徽标跟着名称牌的锚点走

      const g = ud.localGround.clone()
      if (bInfo && bState) g.applyMatrix4(composeBuildingMatrix(bInfo, bState))
      g.applyMatrix4(toCity)
      ud.ground.copy(g)

      // 分片/单楼缩放后，光圈半径也要跟着变
      const partScale = partState[ud.part] ? partState[ud.part].scale : 1
      const bScale = bState ? bState.scale : 1
      ud.sphereRadius = ud.baseRadius * partScale * bScale
      // 顶点实测的楼体占地对角半径（× 微调缩放，无实测值为 null）：
      // 包围球把楼高/楼群跨度都包了进去，复杂几何的楼按它估光圈会偏小、
      // 环藏在楼体里看不见；实测占地才真正贴合楼体水平轮廓（见 buildingRipple.rippleRadiusForBuilding）
      const fp = MEASURED_FOOTPRINT.get(`${ud.part}|${ud.buildingCode}`)
      ud.footprintRadius = fp
        ? 0.5 * Math.hypot(fp.maxX - fp.minX, fp.maxY - fp.minY) * partScale * bScale
        : null

      // 地理坐标随微调一起变（点击飞过去、面板显示的坐标都跟着走）
      // 场景 (x=东, y=上, z=-北) -> 分片 ENU (E, N, U) 再换算经纬度
      ud.lla = baseENUToLLA(
        new THREE.Vector3(ud.cityPos.x, -ud.cityPos.z, ud.cityPos.y - LABEL_LIFT),
        baseTransform,
      )
    })
  }

  function setPart(dir, next) {
    const info = partMap[dir]
    if (!info) {
      console.warn(`[loadCityTiles] 没有名为 ${dir} 的分片`)
      return false
    }
    const t = Object.assign({}, DEFAULT_PART_TRANSFORM, partState[dir] || {}, next || {})
    t.scale = Math.max(0.2, Math.min(5, Number(t.scale) || 1))
    partState[dir] = t
    info.group.matrix.copy(composePartMatrix(info, t))
    info.group.visible = t.visible !== false
    info.group.updateMatrixWorld(true)
    relayoutLabels(dir)
    return true
  }

  /** 单栋楼微调：key 为 `${分片目录}|${楼号}` */
  function setBuilding(key, next) {
    const info = buildingMap[key]
    if (!info) {
      console.warn(`[loadCityTiles] 没有名为 ${key} 的楼`)
      return false
    }
    const t = Object.assign({}, DEFAULT_PART_TRANSFORM, buildingState[key] || {}, next || {})
    t.scale = Math.max(0.2, Math.min(5, Number(t.scale) || 1))
    buildingState[key] = t
    // 楼体 Group 挂在 GLB 内部（分片 ENU 空间），偏移量按该楼的 meshRot 换算过去
    const c = info.localCenter
    const off = new THREE.Vector3(t.east, t.north, t.up)
    if (info.meshRot) off.applyMatrix4(info.meshRot)
    info.groups.forEach((g) => {
      g.position.set(c.x + off.x, c.y + off.y, c.z + off.z)
      g.scale.setScalar(t.scale)
      g.visible = t.visible !== false // 支持单栋楼显示/隐藏
      g.updateMatrixWorld(true)
    })
    relayoutLabels(info.part, info.code)
    return true
  }

  const partList = Object.keys(partMap).map((dir) => ({ dir, name: partMap[dir].name }))
  const buildingList = Object.keys(buildingMap).map((key) => ({
    key,
    code: buildingMap[key].code,
    name: buildingMap[key].name,
    part: buildingMap[key].part,
  }))

  const empty = () => Object.assign({}, DEFAULT_PART_TRANSFORM)

  return {
    parts: {
      list: partList,
      set: setPart,
      get: (dir) => Object.assign(empty(), partState[dir] || {}),
      all: () => partList.map((p) => Object.assign({ dir: p.dir, name: p.name }, empty(), partState[p.dir] || {})),
      reset: (dir) => setPart(dir, empty()),
    },
    buildings: {
      list: buildingList,
      /** 用于射线拾取的 mesh 列表 */
      pickMeshes: () => {
        const out = []
        buildingList.forEach((b) => {
          const info = buildingMap[b.key]
          if (info) info.meshes.forEach((m) => out.push(m))
        })
        return out
      },
      meshOf: (key) => {
        const info = buildingMap[key]
        return info ? info.meshes[0] : null
      },
      /** 调试用：某栋楼中心在世界坐标里的位置（用来核对名称牌是否在楼正上方） */
      worldPosOf: (key) => {
        const info = buildingMap[key]
        if (!info || !info.groups.length) return null
        info.groups[0].updateWorldMatrix(true, false)
        return new THREE.Vector3().setFromMatrixPosition(info.groups[0].matrixWorld)
      },
      /** 只要这栋楼的 mesh 列表（选中态换材质用）：不碰几何、不算包围盒 */
      meshesOf: (code) => {
        const key = buildingKeyByCode(buildingMap, code)
        const info = key ? buildingMap[key] : null
        return info && info.meshes.length ? info.meshes : null
      },
      /**
       * 详情视图用：这栋楼的**真实世界位形** + 包围盒。
       * 一块楼的几何散在多个 GLB、多个父节点里（父级矩阵各不相同），所以这里不克隆
       * 层级，而是逐 mesh 抄下 matrixWorld —— 拿到手直接当局部矩阵用，父级给单位阵，
       * 拼出来就是它在世界里的样子，不会"裂开"也不会歪。
       * @returns {{meshes:THREE.Mesh[], pieces:{geometry:THREE.BufferGeometry, material:THREE.Material, matrix:THREE.Matrix4}[], box:THREE.Box3}|null}
       */
      sourceOf: (code) => {
        const key = buildingKeyByCode(buildingMap, code)
        const info = key ? buildingMap[key] : null
        if (!info || !info.meshes.length) return null
        const meshes = []
        const pieces = []
        const box = new THREE.Box3()
        info.meshes.forEach((mesh) => {
          if (!mesh.isMesh || !mesh.geometry || !mesh.geometry.attributes.position) return
          mesh.updateWorldMatrix(true, false)
          const wb = meshWorldBox(mesh)
          if (!wb) return
          meshes.push(mesh)
          // 选中态会把 mesh.material 换成全息材质，详情视图要的是本来那份
          pieces.push({
            geometry: mesh.geometry,
            material: mesh.userData.fsgBaseMaterial || mesh.material,
            matrix: mesh.matrixWorld.clone(),
          })
          box.union(wb)
        })
        return pieces.length && !box.isEmpty() ? { meshes, pieces, box } : null
      },
      set: setBuilding,
      get: (key) => Object.assign(empty(), buildingState[key] || {}),
      all: () => buildingList.map((b) => Object.assign({ key: b.key, code: b.code, name: b.name, part: b.part }, empty(), buildingState[b.key] || {})),
      reset: (key) => setBuilding(key, empty()),
      /** 选中高亮用：楼的世界包围盒（分片微调后也准） */
      relayout: relayoutLabels,
    },
  }
}

// ============ 外立面样式（重点楼保留原色，其余白透体块）============

/** 分片 scenetree 里所有 element 的球心，换算到该分片的局部 ENU（Z-up，与 GLB 顶点同一坐标系） */
function collectPartElements(part, partInv) {
  if (!part.tree) return []
  const out = []
  const walk = (node) => {
    if (!node) return
    if (node.type === 'element' && Array.isArray(node.sphere)) {
      const p = new THREE.Vector3(node.sphere[0], node.sphere[1], node.sphere[2]).applyMatrix4(partInv)
      out.push({
        x: p.x,
        y: p.y,
        z: p.z,
        radius: node.sphere[3],
        code: String(node.name),
        name: buildingDisplayName(node.name), // 无对应名称时为 null
        opaque: isOpaqueCode(node.name),
      })
    }
    const kids = node.children || []
    kids.forEach(walk)
  }
  const scenes = part.tree.scenes || []
  scenes.forEach(walk)
  return out
}

const patchedMaterials = new WeakSet()

// 体块基调色：three 默认开色彩管理，构造函数已把 sRGB 转成线性工作空间，可直接喂 shader
const FACADE_TINT = new THREE.Color(FACADE_STYLE.tintColor)
const FACADE_TINT_LITERAL = `vec3(${FACADE_TINT.r.toFixed(4)}, ${FACADE_TINT.g.toFixed(4)}, ${FACADE_TINT.b.toFixed(4)})`

/**
 * 给材质打补丁：按顶点属性 aKeep（1=保留原色，0=白透）混合颜色与不透明度。
 * 插在 opaque_fragment 之后，仍会经过色调映射与色彩空间转换。
 *
 * ⚠️ 不要在这里加"风格化"（降饱和 / 提亮 / 屋顶自动上色）：试过，不好看 ——
 * 这批 FBX 的照片贴图是客户认自家楼的依据，糊成浅色体块反而丢了识别度。
 * 要改建筑观感请动模型资产，别动着色器。（用户 2026-09-30 明确否掉）
 */
function patchFacadeMaterial(mat) {
  if (!mat || patchedMaterials.has(mat)) return
  patchedMaterials.add(mat)
  mat.transparent = true
  if (FACADE_STYLE.doubleSide) mat.side = THREE.DoubleSide
  mat.customProgramCacheKey = () => 'facade-style'
  mat.onBeforeCompile = (shader) => {
    shader.vertexShader =
      'attribute float aKeep;\nvarying float vKeep;\n' +
      shader.vertexShader.replace('#include <begin_vertex>', '#include <begin_vertex>\n  vKeep = aKeep;')
    shader.fragmentShader = 'varying float vKeep;\n' + shader.fragmentShader
    const inj = `
  {
    float k = clamp(vKeep, 0.0, 1.0);
    gl_FragColor.rgb = mix(${FACADE_TINT_LITERAL}, gl_FragColor.rgb, 1.0 - ${FACADE_STYLE.tintStrength.toFixed(3)} * (1.0 - k));
    gl_FragColor.a *= mix(${FACADE_STYLE.alpha.toFixed(3)}, 1.0, k);
  }`
    if (shader.fragmentShader.indexOf('#include <opaque_fragment>') >= 0) {
      shader.fragmentShader = shader.fragmentShader.replace('#include <opaque_fragment>', `#include <opaque_fragment>${inj}`)
    } else {
      // 兼容旧版 three 的 output_fragment
      shader.fragmentShader = shader.fragmentShader.replace('#include <output_fragment>', `#include <output_fragment>${inj}`)
    }
    // ⚠️ 不要把 shader（或任何大对象）挂到 mat.userData 上"留着以后用"：
    // Material.copy() 对 userData 这种纯对象是走 JSON.parse(JSON.stringify(...)) 深拷的，
    // 而 shader 里是两份完整的 GLSL 源文本（单份几百 KB）。挂上去之后每 clone 一次材质
    // 就要序列化+反序列化几百 KB —— 实测"点一下建筑"因此卡 3.3 秒，还顺带漏一堆内存。
    // 这里没人读它，直接不存。
  }
  mat.needsUpdate = true
}

/** 已经发过光的材质：emissiveMap 挂上去会改着色器 define，只做一次 */
const emissivedMaterials = new WeakSet()

/**
 * 给编号楼的材质开自发光（用户 2026-10-22）。返回 true = 这次真的改了材质。
 *
 * 为什么可以直接改材质而不是塞进 shader：21 个楼号各占一个分片目录，GLTFLoader 的材质
 * 不跨文件共享，所以"这批材质"恰好就是那 21 幢楼自己那批；同一栋楼拆出来的多块 mesh
 * 共用同一份材质，改一次就够。facadeStyle 那层 aKeep 混色在 emissive 之后才乘上去，
 * 万一某个分片里混进了非编号楼的几何，它也会被一起压暗，不会漏出来发光。
 */
function applyFocusEmissive(mat) {
  if (!mat || !mat.map || emissivedMaterials.has(mat)) return false
  emissivedMaterials.add(mat)
  mat.emissiveMap = mat.map
  mat.emissive = new THREE.Color(FOCUS_EMISSIVE.color)
  mat.emissiveIntensity = FOCUS_EMISSIVE.strength
  mat.needsUpdate = true
  return true
}

/**
 * 把一个 GLB 场景按 scenetree 元素拆成"一栋楼一个 mesh"，同时写入 aKeep（外立面样式）。
 *
 * 为什么必须拆：CesiumLab 导出的 GLB 是**一整块合并网格**（part1 的 17 栋楼在同一个 mesh 里），
 * 不拆就没法对单栋楼做位移/缩放。
 *
 * 做法：逐顶点归到"最近的元素球心"，再按三角面所属元素把索引分桶，
 * 每个桶生成一个共享同一份顶点属性（position/normal/uv/aKeep）的新 BufferGeometry
 * —— 不复制顶点数据，只多出几份索引。
 * 每栋楼外套一个 Group（原点放在该楼包围球心），位移/缩放都作用在它上面，
 * 于是缩放天然以楼自身中心为基准，不会整体跑偏。
 *
 * @param {THREE.Object3D} gltfScene
 * @param {Array} elements collectPartElements 的结果
 * @param {boolean} doSplit 分片里没有业务命名的楼时传 false：只写 aKeep，不拆网格
 * @returns {{ buildings: Array, translucent: number }}
 */
function splitSceneByElement(gltfScene, elements, doSplit) {
  const buildings = []
  let translucent = 0
  if (!elements.length) return { buildings, translucent }
  const hasOpaque = elements.some((e) => e.opaque)

  const meshList = []
  gltfScene.traverse((o) => {
    if (o.isMesh && o.geometry && o.geometry.attributes.position) meshList.push(o)
  })

  meshList.forEach((mesh) => {
    const geo = mesh.geometry
    const pos = geo.attributes.position
    const count = pos.count

    // 元素球心（collectPartElements 的结果，分片 ENU 空间）换到该 mesh 的局部空间。
    // 实测：本数据的 GLB 里 rootNode 自带 rotX(-90°)，raw 顶点坐标本身就是分片 ENU（Z-up），
    // 所以这里的映射合成为单位矩阵（保留这个写法是为了别的数据也能算对）。
    mesh.updateWorldMatrix(true, false)
    const meshToPart = new THREE.Matrix4().makeRotationX(Math.PI / 2).multiply(mesh.matrixWorld)
    const tileToMesh = meshToPart.clone().invert()
    const meshRot = new THREE.Matrix4().extractRotation(tileToMesh)
    const local = elements.map((e) => new THREE.Vector3(e.x, e.y, e.z).applyMatrix4(tileToMesh))

    const keep = new Float32Array(count)
    const elemOf = new Int32Array(count)

    if (!hasOpaque) {
      // 整片都要变半透明：不用算距离，全部归到 0 号元素（也不拆网格）
      translucent += count
    } else {
      for (let i = 0; i < count; i++) {
        const x = pos.getX(i)
        const y = pos.getY(i)
        const z = pos.getZ(i)
        let best = Infinity
        let bi = 0
        for (let c = 0; c < local.length; c++) {
          const dx = x - local[c].x
          const dy = y - local[c].y
          const dz = z - local[c].z
          const d = dx * dx + dy * dy + dz * dz
          if (d < best) {
            best = d
            bi = c
          }
        }
        elemOf[i] = bi
        keep[i] = elements[bi].opaque ? 1 : 0
        if (!keep[i]) translucent += 1
      }
    }
    geo.setAttribute('aKeep', new THREE.BufferAttribute(keep, 1))

    if (!doSplit) return // 周边白模这类没有业务命名的分片：保持整块，只做外立面样式

    // 按三角面归属分桶（三个顶点取多数，平票取第一个）
    const srcIndex = geo.index ? geo.index.array : null
    const triCount = Math.floor((srcIndex ? srcIndex.length : count) / 3)
    const buckets = new Map()
    for (let t = 0; t < triCount; t++) {
      const i0 = srcIndex ? srcIndex[t * 3] : t * 3
      const i1 = srcIndex ? srcIndex[t * 3 + 1] : t * 3 + 1
      const i2 = srcIndex ? srcIndex[t * 3 + 2] : t * 3 + 2
      const a = elemOf[i0]
      const b = elemOf[i1]
      const c = elemOf[i2]
      let owner = a
      if (b === c) owner = b
      else if (a === c) owner = c
      let arr = buckets.get(owner)
      if (!arr) {
        arr = []
        buckets.set(owner, arr)
      }
      arr.push(i0, i1, i2)
    }

    const parent = mesh.parent
    buckets.forEach((idx, ei) => {
      const el = elements[ei]
      if (!el) return
      // 实测该楼本块几何的水平包围盒（顶点即分片 ENU，x=东 y=北，原始米）。
      // hasOpaque=false 且多元素时 elemOf 不可信（全归 0 号），只有单元素分片才量。
      let measuredBBox = null
      if (hasOpaque || elements.length === 1) {
        let minX = Infinity
        let maxX = -Infinity
        let minY = Infinity
        let maxY = -Infinity
        for (let vi = 0; vi < idx.length; vi++) {
          const x = pos.getX(idx[vi])
          const y = pos.getY(idx[vi])
          if (x < minX) minX = x
          if (x > maxX) maxX = x
          if (y < minY) minY = y
          if (y > maxY) maxY = y
        }
        if (maxX >= minX && maxY >= minY) measuredBBox = { minX, maxX, minY, maxY }
      }
      const sub = new THREE.BufferGeometry()
      // 共享顶点属性，只挂自己的索引
      Object.keys(geo.attributes).forEach((k) => sub.setAttribute(k, geo.attributes[k]))
      sub.setIndex(new THREE.BufferAttribute(new Uint32Array(idx), 1))

      const subMesh = new THREE.Mesh(sub, mesh.material)
      // 真正的取值在 loadCityTiles 末尾按 SHADOWS 统一刷（这里只表意：编号楼是投影体）
      subMesh.castShadow = !!SHADOWS.enabled
      subMesh.receiveShadow = !!SHADOWS.enabled
      subMesh.userData = {
        buildingCode: el.code,
        buildingName: el.name || '',
        localCenter: local[ei].clone(),
      }
      // 楼外套一层 Group，原点 = 该楼包围球心：位移/缩放都作用在它上面
      const wrap = new THREE.Group()
      wrap.name = `building_${el.code}`
      wrap.position.copy(local[ei])
      subMesh.position.copy(local[ei]).negate()
      wrap.add(subMesh)
      if (parent) parent.add(wrap)
      buildings.push({
        code: el.code,
        name: el.name || '',
        group: wrap,
        mesh: subMesh,
        // Mesh 局部轴（Group 所在层）：把"分片空间"的位移换算到这里要用它
        meshRot,
        localCenter: local[ei].clone(),
        enuCenter: new THREE.Vector3(el.x, el.y, el.z),
        radius: el.radius || 0,
        measuredBBox, // 实测水平占地（分片局部 ENU），供逐楼围栏贴合楼体
      })
    })
    // 原始合并网格撤掉（注意：几何体属性被上面共享了，不能 dispose）
    if (parent) parent.remove(mesh)
  })

  return { buildings, translucent }
}

/**
 * @param {Function} onProgress (loaded, total, label)
 * @returns {Promise<{group:THREE.Group, box:THREE.Box3, meshes:THREE.Mesh[], centerLLA:{lat,lon,alt}, anchor:{enu:THREE.Vector3, transform:THREE.Matrix4}}>}
 */
export async function loadCityTiles(onProgress) {
  const resolveGlb = glbUrlResolver()
  const parts = collectParts()

  // 统计 GLB 总数用于进度
  const totalGlbs = parts.reduce((s, p) => s + p.uris.length, 0)
  let loadedGlbs = 0

  // 分片是 require.context 递归扫出来的：20260913data 下任何 tileset*.json 目录都会被加载渲染，
  // 新增目录（如 20260913test）无需改代码。这里打一行日志便于确认到底加载了哪些。
  console.info(
    `[loadCityTiles] 共 ${parts.length} 个分片 / ${totalGlbs} 个 GLB：` +
    parts.map((p) => `${p.key.replace('./', '')}(x${p.uris.length})`).join('、'),
  )

  // 基准分片（part1）：其余分片对齐到它的 ENU 局部坐标系
  const baseTransform = parts[0].transform
  const baseInv = baseTransform.clone().invert()

  // GLB 内 rootNode 自带 Z-up→Y-up 翻转（rotX(-90°)，GLTFLoader 自动应用）；
  // rotX(+90°) 用于在合成矩阵末尾抵消这次翻转，避免双重翻转导致模型倒置
  const zUpToYUp = new THREE.Matrix4().makeRotationX(-Math.PI / 2)
  const yUpToZUp = new THREE.Matrix4().makeRotationX(Math.PI / 2)

  const loader = new GLTFLoader()
  const loadGlb = (url) =>
    new Promise((resolve, reject) => {
      loader.load(
        url,
        (gltf) => resolve(gltf.scene || gltf.scenes[0]),
        (evt) => {
          if (evt.total && onProgress) {
            const partial = Math.min(0.95, evt.loaded / evt.total)
            onProgress(loadedGlbs + partial, totalGlbs, '加载建筑模型')
          }
        },
        reject,
      )
    })

  const cityGroup = new THREE.Group()
  cityGroup.name = 'cityTiles'
  const meshes = []
  /** 编号楼用到的材质（去重）：sceneView 的 setFacadeEmissive 就调这一批 */
  const cityMaterials = []
  const partMap = {} // 分片目录 -> { name, group, baseMatrix, localCenter }
  const buildingMap = {} // `${分片目录}|${楼号}` -> { code, name, group, mesh, localCenter, radius, part }

  // 每次加载都重算实测占地（热更新/重复加载时防串旧值）
  MEASURED_FOOTPRINT.clear()

  for (const part of parts) {
    // M = zUpToYUp * inverse(T_base) * T_part * yUpToZUp
    // 作用于 gltf.scene（其内部 rootNode 已应用 rotX(-90°)，与末尾 rotX(+90°) 相消）：
    // 合成效果 = inverse(T_base) * T_part 的纯地理对齐，再整体转成 Y-up
    const align = new THREE.Matrix4().multiplyMatrices(baseInv, part.transform)
    const finalM = new THREE.Matrix4().multiplyMatrices(zUpToYUp, align).multiply(yUpToZUp)

    const partGroup = new THREE.Group()
    partGroup.name = part.key.replace('./', '').replace(/\/tileset[\w-]*\.json$/, '')
    partGroup.matrixAutoUpdate = false
    partGroup.matrix.copy(finalM)

    // 记录分片信息，供"单模型微调"用：baseMatrix 是数据自洽的原始矩阵，
    // localCenter 是该分片包围盒中心（局部 ENU，Z-up）—— 缩放以它为中心，楼群不会飞走
    partMap[part.dir] = {
      name: partGroup.name,
      group: partGroup,
      baseMatrix: finalM.clone(),
      localCenter: part.box
        ? new THREE.Vector3(part.box[0], part.box[1], part.box[2])
        : new THREE.Vector3(),
    }

    // 外立面样式 + 单栋楼拆分：本分片的元素球心（局部 ENU）
    const partInv = part.transform.clone().invert()
    const elements = collectPartElements(part, partInv)
    const opaqueCount = elements.filter((e) => e.opaque).length
    // 只有带业务命名的分片才拆网格（周边白模是 Mesh_N，拆了没意义还多几百个 draw call）
    const shouldSplit = elements.some((e) => e.name)

    for (const uri of part.uris) {
      const url = resolveGlb(part.key, uri)
      if (!url) continue
      const scene = await loadGlb(url)
      const { buildings, translucent } = splitSceneByElement(scene, elements, shouldSplit)
      if (facadeStyleEnabled()) {
        scene.traverse((o) => {
          if (!o.isMesh) return
          const mats = Array.isArray(o.material) ? o.material : [o.material]
          mats.forEach(patchFacadeMaterial)
        })
      }
      // 自发光 + 阴影的落点：这里的每个 mesh 都属于 21 个编号楼之一
      scene.traverse((o) => {
        if (!o.isMesh) return
        const mats = Array.isArray(o.material) ? o.material : [o.material]
        mats.forEach((m) => {
          if (applyFocusEmissive(m) && m && cityMaterials.indexOf(m) < 0) cityMaterials.push(m)
        })
        o.castShadow = !!SHADOWS.enabled
        o.receiveShadow = !!SHADOWS.enabled
      })
      buildings.forEach((b) => {
        b.mesh.userData.part = part.dir // 让点击拾取能精确定位到"哪个分片的哪栋楼"
        const key = `${part.dir}|${b.code}`
        // 实测占地并进注册表（同一栋楼多块 GLB / 多个桶，取并集；null 时跳过）
        mergeMeasuredFootprint(key, b.measuredBBox)
        // 同一栋楼的几何会分散在一个分片的多个 GLB 里，所以按楼把多块一起收进来，
        // 微调时统一作用，否则挪动时楼会"裂开"
        if (!buildingMap[key]) {
          buildingMap[key] = {
            key,
            code: b.code,
            name: b.name,
            part: part.dir,
            localCenter: b.localCenter,
            enuCenter: b.enuCenter,
            radius: b.radius,
            groups: [],
            meshes: [],
          }
        }
        buildingMap[key].groups.push(b.group)
        buildingMap[key].meshes.push(b.mesh)
      })
      if (elements.length) {
        console.info(
          `[loadCityTiles] ${partGroup.name}：元素 ${elements.length} 个（重点楼 ${opaqueCount} 个）` +
          (shouldSplit ? `，拆出可单独微调的楼 ${buildings.length} 栋` : '，整块保留') +
          `，半透明顶点 ${translucent}`,
        )
      }
      scene.traverse((o) => {
        if (o.isMesh) {
          // 编号楼是阴影的主角：投影 + 被邻楼挡影（立体感全靠这两条）
          o.castShadow = !!SHADOWS.enabled
          o.receiveShadow = !!SHADOWS.enabled
          meshes.push(o)
        }
      })
      partGroup.add(scene)
      loadedGlbs += 1
      if (onProgress) onProgress(loadedGlbs, totalGlbs, '加载建筑模型')
    }
    cityGroup.add(partGroup)
  }

  // 居中：x/z 中心置于原点，地面(y最小值)贴 y=0
  cityGroup.updateWorldMatrix(true, true)
  const box = new THREE.Box3().setFromObject(cityGroup)
  const center = box.getCenter(new THREE.Vector3())
  cityGroup.position.set(-center.x, -box.min.y, -center.z)
  cityGroup.updateWorldMatrix(true, true)
  const finalBox = new THREE.Box3().setFromObject(cityGroup)

  // cityGroup 局部坐标 = (东, 上, -北)，故场景原点对应的基准分片 ENU 坐标 = (center.x, -center.z, box.min.y)
  const anchor = { enu: new THREE.Vector3(center.x, -center.z, box.min.y), transform: baseTransform.clone() }
  // 用真实几何算出的精确锚点经纬度（元数据估算值只用于地图首次定位，这里是最终值）
  const centerLLA = anchorLLA(anchor, null)

  // 建筑名称标牌：必须在算完包围盒之后再挂，否则会把标牌算进模型包围盒、把居中算歪。
  // 末尾补上 EXTRA_LABELS 里没有独立几何的楼号（如几何并入别楼的 4 号楼）
  const labelAnchors = applyExtraLabels(collectLabelAnchors(parts, baseInv))
  let labelGroup = null
  if (labelAnchors.length) {
    labelGroup = buildLabelGroup(labelAnchors, baseTransform, (dir) => partMap[dir].group.matrix)
    cityGroup.add(labelGroup)
    console.info(
      `[loadCityTiles] 建筑标牌 ${labelAnchors.length} 块：` + labelAnchors.map((a) => a.name).join('、'),
    )
  } else {
    console.warn('[loadCityTiles] scenetree 里没有可用的建筑名称，跳过名称标牌')
  }

  const controls = createTransformControls(partMap, buildingMap, labelGroup, baseTransform)
  controls.buildings.relayout() // 首次铺开标牌（同时写入地面点与经纬度）
  console.info(
    `[loadCityTiles] 可微调：分片 ${controls.parts.list.length} 个、单栋楼 ${controls.buildings.list.length} 栋`,
  )

  if (onProgress) onProgress(totalGlbs, totalGlbs, '模型加载完成')

  return {
    group: cityGroup,
    box: finalBox,
    meshes,
    materials: cityMaterials,
    centerLLA,
    anchor,
    labelGroup,
    labelAnchors,
    /**
     * 微调控制器：
     *   controls.parts.set(分片目录, { east, north, up, scale })
     *   controls.buildings.set('20260913part1/|13', { east, north, up, scale })
     */
    controls,
  }
}
